import { useState, useEffect } from "react";
import Select from "react-select";
import { useApiRequestMutation } from "../../services/apiService";
import ToastrService from "../../services/toastService";
import { isSuperAdmin } from "../../services/utils";

const customSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        borderColor: state.isFocused ? "#0782D5" : provided.borderColor,
    }),

    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected
            ? "#0782D5"
            : state.isFocused
                ? "#0782d57a"
                : provided.backgroundColor,
        color: state.isSelected || state.isFocused ? "#fff" : "#000",
        cursor: "pointer",
    }),
};

const UserDrawer = ({
    mode = "add",
    isReadOnly = false,
    roleList = [],
    onClose,
    onSave,
    userData = null,
}) => {
    const isEdit = mode !== "add";
    const superAdmin = isSuperAdmin();

    const [apiRequest, { isLoading }] = useApiRequestMutation();

    const [reportingOptions, setReportingOptions] = useState([]);

    // const [reportingUser, setReportingUser] = useState(null);
    // const [campaignOptions, setCampaignOptions] = useState([]);

    const [statusOptions] = useState([
        { value: "active", label: "Active" },
        { value: "inactive", label: "Inactive" }
    ]);

    const [initialForm, setInitialForm] = useState(null);

    const today = new Date();
    const maxDate = today.toISOString().split("T")[0];

    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    const minDate = oneMonthAgo.toISOString().split("T")[0];

    const [userForm, setUserForm] = useState({
        employeeId: "",
        dateOfJoining: "",
        firstName: "",
        lastName: "",
        email: "",
        role: null,

        // MULTIPLE SECONDARY ROLES
        secondaryRole: [],

        reportingUser: null,
        status: null,
        // campaign: null,
        // department: null
    });

    const [errors, setErrors] = useState({
        employeeId: "",
        dateOfJoining: "",
        firstName: "",
        lastName: "",
        email: "",
        role: "",
        secondaryRole: "",
        reportingUser: "",
        // campaign: "",
        // department: ""
    });

    const [roleOptions, setRoleOptions] = useState([]);

    // =====================================================
    // SECONDARY ROLE OPTIONS
    // =====================================================

    const [secondaryRoleOptions, setSecondaryRoleOptions] = useState([]);

    // =====================================================
    // INITIAL USER DATA
    // =====================================================

    useEffect(() => {
        if (userData) {

            const formData = {
                employeeId: userData.employeeId || "",
                firstName: userData.firstName || "",
                lastName: userData.lastName || "",
                email: userData.email || "",

                role: userData.role
                    ? {
                        value: userData.role,
                        label: userData.role
                    }
                    : null,

                // Keep as array for multi-select
                secondaryRole: [],

                reportingUser: userData.reported_by
                    ? {
                        value: userData.reported_by,
                        label: userData.reported_by
                    }
                    : null,

                status: statusOptions.find(
                    (opt) => opt.value === userData.status
                ) || null,

                dateOfJoining: userData.dateOfJoining
                    ? userData.dateOfJoining.split("T")[0]
                    : "",

                // campaign: campaignOptions.find((opt) => opt.id === userData.campaign_id)

                // department: userData.department_id
                //     ? {
                //         value: userData.department_id,
                //         label: userData.department_name
                //     }
                //     : null,
            };

            setUserForm(formData);
            setInitialForm(formData);
        }
    }, [userData]);

    // =====================================================
    // VALIDATION HELPERS
    // =====================================================

    const validateName = (name) => /^[A-Za-z\s]+$/.test(name);

    const validateEmail = (email) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const validateField = (field, value) => {
        let message = "";

        if (field === "employeeId") {
            if (!value.trim()) {
                message = "Employee ID is required";
            }
        }

        if (field === "dateOfJoining") {
            if (!value) {
                message = "Date of Joining is required";
            } else if (isFutureDate(value)) {
                message =
                    "Date of joining must be within last 30 days and not in future";
            }
        }

        if (field === "firstName") {
            if (!value.trim()) {
                message = "First Name is required";
            } else if (!validateName(value)) {
                message = "First Name should contain only alphabets";
            }
        }

        if (field === "lastName") {
            if (!value.trim()) {
                message = "Last Name is required";
            } else if (!validateName(value)) {
                message = "Last Name should contain only alphabets";
            }
        }

        if (field === "email") {
            if (!value.trim()) {
                message = "Email is required";
            } else if (!validateEmail(value)) {
                message = "Enter valid email address";
            }
        }

        if (field === "role") {
            if (!value) {
                message = "Role is required";
            }
        }

        if (field === "reportingUser") {
            if (!value) {
                message = "Reporting User is required";
            }
        }

        if (field === "status") {
            if (!value) {
                message = "Status is required";
            }
        }

        // =====================================================
        // MULTIPLE SECONDARY ROLES VALIDATION
        // =====================================================

        if (field === "secondaryRole") {
            if (
                !Array.isArray(value) ||
                value.length === 0
            ) {
                message = "At least one Secondary Role is required";
            }
        }

        // if (field === "campaign") {
        //     if (!value) message = "Campaign is required";
        // }

        // if (field === 'department') {
        //     if (!value) message = 'Department is required'
        // }

        setErrors((prev) => ({
            ...prev,
            [field]: message
        }));
    };

    // =====================================================
    // GENERIC FORM CHANGE
    // =====================================================

    const handleChange = (field, value) => {
        setUserForm((prev) => ({
            ...prev,
            [field]: value
        }));

        validateField(field, value);
    };

    // =====================================================
    // HANDLE SUBMIT
    // =====================================================

    const handleSubmit = async (e) => {
        e.preventDefault();

        validateField("firstName", userForm.firstName);
        validateField("lastName", userForm.lastName);
        validateField("email", userForm.email);
        validateField("role", userForm.role);
        validateField("employeeId", userForm.employeeId);

        if (!isEdit) {
            validateField(
                "dateOfJoining",
                userForm.dateOfJoining
            );

            if (isFutureDate(userForm.dateOfJoining)) {
                return;
            }
        }

        if (!superAdmin) {

            // =================================================
            // SECONDARY ROLE VALIDATION
            // =================================================

            if (secondaryRoleOptions.length > 0) {
                if (
                    !Array.isArray(userForm.secondaryRole) ||
                    userForm.secondaryRole.length === 0
                ) {
                    validateField(
                        "secondaryRole",
                        userForm.secondaryRole
                    );

                    return;
                }
            }

            // validateField("department", userForm.department);

            // validateField("campaign", userForm.campaign);

            validateField(
                "reportingUser",
                userForm.reportingUser
            );

            if (!userForm.reportingUser) {
                return;
            }
        }

        // =====================================================
        // EDIT
        // =====================================================

        if (isEdit) {

            if (!userForm.role) {
                validateField("role", userForm.role);
                return;
            }

            if (
                userData?.status !== "unconfirmed" &&
                userData?.status !== "deleted" &&
                !userForm.status
            ) {
                validateField(
                    "status",
                    userForm.status
                );

                return;
            }

            if (!superAdmin) {

                if (!userForm.reportingUser) {
                    validateField(
                        "reportingUser",
                        userForm.reportingUser
                    );

                    return;
                }

                // =================================================
                // MULTIPLE SECONDARY ROLE VALIDATION
                // =================================================

                if (
                    secondaryRoleOptions.length > 0 &&
                    (
                        !Array.isArray(userForm.secondaryRole) ||
                        userForm.secondaryRole.length === 0
                    )
                ) {
                    validateField(
                        "secondaryRole",
                        userForm.secondaryRole
                    );

                    return;
                }
            }

        } else {

            // =====================================================
            // ADD
            // =====================================================

            const basicValidation =
                !userForm.employeeId ||
                !userForm.dateOfJoining ||
                !userForm.firstName ||
                !userForm.lastName ||
                !validateEmail(userForm.email) ||
                !userForm.role;

            if (basicValidation) {
                return;
            }

            if (!superAdmin) {

                const secondaryRoleRequired =
                    secondaryRoleOptions.length > 0 &&
                    (
                        !Array.isArray(userForm.secondaryRole) ||
                        userForm.secondaryRole.length === 0
                    );

                if (
                    !userForm.reportingUser ||
                    secondaryRoleRequired
                ) {
                    return;
                }
            }
        }

        // =====================================================
        // PAYLOAD
        // =====================================================

        let payload = {};

        // =====================================================
        // EDIT PAYLOAD
        // =====================================================

        if (isEdit) {

            payload = {
                id: userData?.id,
                primary_role: userForm.role?.value,
            };

            // Status should only be sent when available

            if (
                userData?.status !== "unconfirmed" &&
                userData?.status !== "deleted" &&
                userForm.status?.value
            ) {
                payload.status = userForm.status.value;
            }

            if (!superAdmin) {

                payload.reported_by =
                    userForm.reportingUser?.value;

                // =================================================
                // SEND MULTIPLE SECONDARY ROLE IDS
                // =================================================

                payload.secondary_role =
                    Array.isArray(userForm.secondaryRole)
                        ? userForm.secondaryRole.map(
                            (role) => role.value
                        )
                        : [];

                // payload.campaign_id =
                //     userForm.campaign?.value;

                // payload.department_id =
                //     userForm.department?.value;
            }

        } else {

            // =====================================================
            // ADD PAYLOAD
            // =====================================================

            payload = {
                employee_id: userForm.employeeId,
                date_of_joining: userForm.dateOfJoining,
                first_name: userForm.firstName,
                last_name: userForm.lastName,
                email: userForm.email,

                primary_role:
                    userForm.role?.value || "",

                primary_role_name:
                    userForm.role?.roleName || "",
            };

            if (!superAdmin) {

                // =================================================
                // SEND MULTIPLE SECONDARY ROLE IDS
                // =================================================

                payload.secondary_role =
                    Array.isArray(userForm.secondaryRole)
                        ? userForm.secondaryRole.map(
                            (role) => role.value
                        )
                        : [];

                // payload.campaign_id =
                //     userForm.campaign?.value;

                payload.reported_by =
                    userForm.reportingUser?.value;

                // payload.department_id =
                //     userForm.department?.value;
            }
        }

        // =====================================================
        // API CALL
        // =====================================================

        try {

            const res = await apiRequest({
                url: isEdit
                    ? "/admin/editUser"
                    : "/admin/addUser",

                method: "POST",

                data: payload
            }).unwrap();

            const message =
                res?.message ||
                res?.data?.message ||
                "User created successfully";

            ToastrService.success(message);

            if (onSave) {
                onSave(res);
            }

            handleReset();

            onClose();

        } catch (error) {

            console.error(
                "Create User Error:",
                error
            );

            const message =
                error?.data?.message ||
                error?.message ||
                "Failed to create user";

            ToastrService.error(message);
        }
    };

    // =====================================================
    // RESET
    // =====================================================

    const handleReset = () => {

        setUserForm({
            employeeId: "",
            dateOfJoining: "",
            firstName: "",
            lastName: "",
            email: "",
            role: null,

            // MULTIPLE SECONDARY ROLES
            secondaryRole: [],

            reportingUser: null,
            status: null,
            // campaign: null
        });

        setErrors({
            employeeId: "",
            dateOfJoining: "",
            firstName: "",
            lastName: "",
            email: "",
            role: "",
            secondaryRole: "",
            reportingUser: "",
            status: "",
            // campaign: ""
        });

        setReportingOptions([]);

        // =====================================================
        // CLEAR SECONDARY ROLE OPTIONS
        // =====================================================

        setSecondaryRoleOptions([]);
    };

    // =====================================================
    // DATE VALIDATION
    // =====================================================

    const isFutureDate = (date) => {

        if (!date) {
            return false;
        }

        const selected = new Date(date);
        const today = new Date();

        selected.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        const oneMonthAgo = new Date(today);

        oneMonthAgo.setMonth(
            today.getMonth() - 1
        );

        return (
            selected > today ||
            selected < oneMonthAgo
        );
    };

    // =====================================================
    // GET ALL ROLES
    // =====================================================

    const getAllRoles = async () => {

        try {

            const res = await apiRequest({
                url: "/role/getAllActiveRole",
                method: "GET",
            }).unwrap();

            const roles =
                res?.data?.roles || [];

            // const campaigns =
            //     res?.data?.campaign || [];

            // =================================================
            // PRIMARY ROLE OPTIONS
            // =================================================

            const roleOptions = roles.map((role) => ({

                value: role.id,

                label:
                    role.role_displayname,

                roleName:
                    role.role_name,

                // =================================================
                // KEEP SECONDARY ROLE DETAILS
                // =================================================

                secondaryRoles:
                    Array.isArray(role.secondary_role)
                        ? role.secondary_role
                        : []

            }));

            setRoleOptions(
                roleOptions
            );

            // =================================================
            // CAMPAIGN OPTIONS
            // =================================================

            // const campaignOptions =
            //     campaigns.map((camp) => ({
            //         value: camp.id,
            //         label: camp.name
            //     }));

            // =================================================
            // SET EDIT PRIMARY ROLE
            // =================================================

            if (userData?.role) {

                const selectedRole =
                    roleOptions.find(
                        (opt) =>
                            opt.roleName ===
                            userData.role
                    );

                if (selectedRole) {

                    setUserForm((prev) => ({
                        ...prev,
                        role: selectedRole,
                    }));

                    // =================================================
                    // SET SECONDARY OPTIONS
                    // =================================================

                    setSecondaryRoleOptions(
                        getSecondaryRoleOptions(
                            selectedRole
                        )
                    );
                }
            }

        } catch (error) {

            console.error(
                "Fetch Roles Error:",
                error
            );
        }
    };

    // =====================================================
    // CONVERT SECONDARY ROLE DETAILS TO SELECT OPTIONS
    // =====================================================

    const getSecondaryRoleOptions = (
        selectedRole
    ) => {

        if (!selectedRole) {
            return [];
        }

        const secondaryRoles =
            Array.isArray(
                selectedRole.secondaryRoles
            )
                ? selectedRole.secondaryRoles
                : [];

        if (!secondaryRoles.length) {
            return [];
        }

        return secondaryRoles
            .map((secondaryRole) => ({

                value:
                    secondaryRole.id,

                label:
                    secondaryRole.role_displayname ||
                    secondaryRole.role_name,

                roleName:
                    secondaryRole.role_name

            }))
            .filter(
                (option) =>
                    Number(option.value) !==
                    Number(selectedRole.value)
            );
    };

    // =====================================================
    // UPDATE SECONDARY ROLE OPTIONS
    // =====================================================

    const updateSecondaryRoleOptions = (
        selectedRole
    ) => {

        const options =
            getSecondaryRoleOptions(
                selectedRole
            );

        setSecondaryRoleOptions(
            options
        );

        // =================================================
        // CLEAR OLD SECONDARY ROLES
        // =================================================

        setUserForm((prev) => ({
            ...prev,
            secondaryRole: []
        }));

        // =================================================
        // CLEAR SECONDARY ROLE ERROR
        // =================================================

        setErrors((prev) => ({
            ...prev,
            secondaryRole: ""
        }));
    };

    // =====================================================
    // LOAD ROLES
    // =====================================================

    useEffect(() => {

        getAllRoles();

        // if (!superAdmin) {
        //     getDepartment();
        // }

    }, [superAdmin]);

    // =====================================================
    // DEPARTMENT
    // =====================================================

    // const [department, setDepartment] = useState([])

    // const getDepartment = async () => {
    //     try {
    //         const response = await apiRequest({
    //             url: "/dropdown/getDropdown",
    //             method: "POST",
    //             data: {
    //                 category: ["department"]
    //             }
    //         }).unwrap();

    //         if (response.success) {
    //             const deptOptions =
    //                 (response?.data?.department || [])
    //                     .map((dept) => ({
    //                         label: dept.name,
    //                         value: dept.id
    //                     }));

    //             setDepartment(deptOptions);
    //         }

    //     } catch (error) {
    //         console.error(
    //             "Fetch Roles Error:",
    //             error
    //         );
    //     }
    // };

    // =====================================================
    // FORM VALIDATION
    // =====================================================

    const isFormValid =
        userForm.employeeId &&
        userForm.dateOfJoining &&
        !isFutureDate(
            userForm.dateOfJoining
        ) &&
        userForm.firstName &&
        userForm.lastName &&
        validateEmail(
            userForm.email
        ) &&
        userForm.role;

    // =====================================================
    // GET REPORTING USERS
    // =====================================================

    const getReportingUsers = async (
        roleId
    ) => {

        try {

            let reportingUserPayload = {
                role: roleId,
                userId:
                    userData &&
                        userData.id
                        ? userData.id
                        : ""
            };

            const res =
                await apiRequest({
                    url: `/admin/getReportingUsers`,
                    method: "POST",
                    data: reportingUserPayload
                }).unwrap();

            const options =
                res?.data?.map(
                    (user) => ({
                        value: user.id,
                        label:
                            `${user.name} - ${user.email}`
                    })
                ) || [];

            setReportingOptions(
                options
            );

        } catch (error) {

            console.error(
                "Reporting Users Fetch Error:",
                error
            );
        }
    };

    // =====================================================
    // INITIALIZE EDIT DATA
    // =====================================================

    useEffect(() => {

        const initEditData = async () => {

            if (
                !userData ||
                roleOptions.length === 0
            ) {
                return;
            }

            // =================================================
            // FIND PRIMARY ROLE
            // =================================================

            const selectedRole =
                roleOptions.find(
                    (opt) =>
                        opt.roleName ===
                        userData.role
                );

            if (!selectedRole) {
                return;
            }

            // =================================================
            // SET PRIMARY ROLE
            // =================================================

            setUserForm((prev) => ({
                ...prev,
                role: selectedRole
            }));

            // =================================================
            // SET SECONDARY ROLE OPTIONS
            // =================================================

            const secondaryOptions =
                getSecondaryRoleOptions(
                    selectedRole
                );

            setSecondaryRoleOptions(
                secondaryOptions
            );

            // =================================================
            // SET EXISTING SECONDARY ROLES
            // =================================================

            let existingSecondaryRoles = [];

            // Backend can return:
            //
            // [
            //     {
            //         id: 4,
            //         role_name: "supervisor",
            //         role_displayname: "Supervisor"
            //     },
            //     {
            //         id: 7,
            //         role_name: "rta",
            //         role_displayname: "RTA"
            //     }
            // ]
            //
            // OR:
            //
            // [4, 7, 6]

            if (Array.isArray(userData.secondary_role)) {

                if (
                    userData.secondary_role.length > 0 &&
                    typeof userData.secondary_role[0] === "object"
                ) {

                    existingSecondaryRoles =
                        userData.secondary_role
                            .map((secondaryRole) => {

                                return secondaryOptions.find(
                                    (option) =>
                                        Number(option.value) ===
                                        Number(secondaryRole.id)
                                );

                            })
                            .filter(Boolean);

                } else {

                    existingSecondaryRoles =
                        userData.secondary_role
                            .map((secondaryRoleId) => {

                                return secondaryOptions.find(
                                    (option) =>
                                        Number(option.value) ===
                                        Number(secondaryRoleId)
                                );

                            })
                            .filter(Boolean);
                }

            } else if (
                userData.secondary_role !== null &&
                userData.secondary_role !== undefined &&
                userData.secondary_role !== ""
            ) {

                // Backward compatibility if API
                // returns a single ID

                const selectedSecondaryRole =
                    secondaryOptions.find(
                        (opt) =>
                            Number(opt.value) ===
                            Number(userData.secondary_role)
                    );

                if (selectedSecondaryRole) {
                    existingSecondaryRoles = [
                        selectedSecondaryRole
                    ];
                }
            }

            setUserForm((prev) => ({
                ...prev,
                secondaryRole:
                    existingSecondaryRoles
            }));

            // =================================================
            // SUPER ADMIN
            // =================================================

            if (superAdmin) {
                return;
            }

            // =================================================
            // GET REPORTING USERS
            // =================================================

            const res =
                await apiRequest({
                    url: `/admin/getReportingUsers`,
                    method: "POST",

                    data: {
                        role:
                            selectedRole.value,

                        userId:
                            userData.id
                    }
                }).unwrap();

            const options =
                res?.data?.map(
                    (user) => ({
                        value: user.id,
                        label:
                            `${user.name} - ${user.email}`
                    })
                ) || [];

            setReportingOptions(
                options
            );

            // =================================================
            // SET REPORTING USER
            // =================================================

            const selectedReportingUser =
                options.find(
                    (opt) =>
                        opt.value ===
                        Number(
                            userData.reported_by
                        )
                );

            setUserForm((prev) => ({
                ...prev,
                reportingUser:
                    selectedReportingUser ||
                    null
            }));
        };

        initEditData();

    }, [
        userData,
        roleOptions,
        superAdmin
    ]);

    // =====================================================
    // FORM CHANGE DETECTION
    // =====================================================

    const isFormChanged = () => {

        if (!initialForm) {
            return false;
        }

        if (superAdmin) {

            return (
                initialForm.role?.value !==
                userForm.role?.value ||

                initialForm.status?.value !==
                userForm.status?.value
            );
        }

        // =====================================================
        // COMPARE MULTIPLE SECONDARY ROLES
        // =====================================================

        const initialSecondaryRoles =
            Array.isArray(initialForm.secondaryRole)
                ? initialForm.secondaryRole
                    .map((role) => Number(role.value))
                    .sort((a, b) => a - b)
                : [];

        const currentSecondaryRoles =
            Array.isArray(userForm.secondaryRole)
                ? userForm.secondaryRole
                    .map((role) => Number(role.value))
                    .sort((a, b) => a - b)
                : [];

        const secondaryRoleChanged =
            JSON.stringify(initialSecondaryRoles) !==
            JSON.stringify(currentSecondaryRoles);

        return (
            initialForm.role?.value !==
            userForm.role?.value ||

            secondaryRoleChanged ||

            initialForm.reportingUser?.value !==
            userForm.reportingUser?.value ||

            initialForm.status?.value !==
            userForm.status?.value
        );
    };

    // =====================================================
    // UI
    // =====================================================

    return (
        <>
            <div
                className="offcanvas offcanvas-end show"
                style={{
                    visibility: "visible"
                }}
                tabIndex="-1"
            >

                {/* ================================================= */}
                {/* HEADER */}
                {/* ================================================= */}

                <div className="offcanvas-header px-4 py-3">

                    <div>

                        <h3 className="mb-1 drawer-header">

                            {mode === "add"
                                ? "Add New User"
                                : "Manage All Users"}

                        </h3>

                        <small className="text-muted">
                            Fill in the details below to create a New User.
                        </small>

                    </div>

                    <button
                        type="button"
                        className="btn-close text-reset"
                        onClick={onClose}
                        aria-label="Close"
                    ></button>

                </div>

                {/* ================================================= */}
                {/* BODY */}
                {/* ================================================= */}

                <div className="offcanvas-body px-4 py-3">

                    <h5 className="mt-2">
                        User Information
                    </h5>

                    <form
                        onSubmit={handleSubmit}
                        autoComplete="off"
                        noValidate
                    >

                        <fieldset disabled={isLoading}>

                            {/* ================================================= */}
                            {/* FIRST NAME / LAST NAME */}
                            {/* ================================================= */}

                            <div className="row g-3 mb-3">

                                <div className="col-md-6">

                                    <label className="form-label">
                                        First Name{" "}
                                        <span className="text-danger">
                                            *
                                        </span>
                                    </label>

                                    <input
                                        type="text"
                                        className={`form-control ${errors.firstName
                                            ? "is-invalid"
                                            : ""
                                            }`}
                                        value={
                                            userForm.firstName
                                        }
                                        disabled={
                                            isReadOnly ||
                                            isEdit
                                        }
                                        onChange={(e) =>
                                            handleChange(
                                                "firstName",
                                                e.target.value
                                            )
                                        }
                                    />

                                    {errors.firstName && (
                                        <div className="invalid-feedback">
                                            {
                                                errors.firstName
                                            }
                                        </div>
                                    )}

                                </div>

                                <div className="col-md-6">

                                    <label className="form-label">
                                        Last Name{" "}
                                        <span className="text-danger">
                                            *
                                        </span>
                                    </label>

                                    <input
                                        type="text"
                                        className={`form-control ${errors.lastName
                                            ? "is-invalid"
                                            : ""
                                            }`}
                                        value={
                                            userForm.lastName
                                        }
                                        disabled={
                                            isReadOnly ||
                                            isEdit
                                        }
                                        onChange={(e) =>
                                            handleChange(
                                                "lastName",
                                                e.target.value
                                            )
                                        }
                                    />

                                    {errors.lastName && (
                                        <div className="invalid-feedback">
                                            {
                                                errors.lastName
                                            }
                                        </div>
                                    )}

                                </div>

                            </div>

                            {/* ================================================= */}
                            {/* EMPLOYEE ID / DATE OF JOINING */}
                            {/* ================================================= */}

                            <div className="row g-3 mb-3">

                                <div className="col-md-6">

                                    <label className="form-label">
                                        Employee ID{" "}
                                        <span className="text-danger">
                                            *
                                        </span>
                                    </label>

                                    <input
                                        type="text"
                                        className={`form-control ${errors.employeeId
                                            ? "is-invalid"
                                            : ""
                                            }`}
                                        value={
                                            userForm.employeeId
                                        }
                                        disabled={
                                            isReadOnly ||
                                            isEdit
                                        }
                                        onKeyDown={(e) => {
                                            if (
                                                e.key ===
                                                " "
                                            ) {
                                                e.preventDefault();
                                            }
                                        }}
                                        onChange={(e) => {

                                            const value =
                                                e.target.value.replace(
                                                    /\s/g,
                                                    ""
                                                );

                                            handleChange(
                                                "employeeId",
                                                value
                                            );
                                        }}
                                    />

                                    {errors.employeeId && (
                                        <div className="invalid-feedback">
                                            {
                                                errors.employeeId
                                            }
                                        </div>
                                    )}

                                </div>

                                <div className="col-md-6">

                                    <label className="form-label">
                                        Date of Joining{" "}
                                        <span className="text-danger">
                                            *
                                        </span>
                                    </label>

                                    <input
                                        type="date"
                                        className={`form-control ${errors.dateOfJoining
                                            ? "is-invalid"
                                            : ""
                                            }`}
                                        value={
                                            userForm.dateOfJoining
                                        }
                                        min={minDate}
                                        max={maxDate}
                                        disabled={
                                            isReadOnly ||
                                            isEdit
                                        }
                                        onChange={(e) =>
                                            handleChange(
                                                "dateOfJoining",
                                                e.target.value
                                            )
                                        }
                                    />

                                    {errors.dateOfJoining && (
                                        <div className="invalid-feedback">
                                            {
                                                errors.dateOfJoining
                                            }
                                        </div>
                                    )}

                                </div>

                            </div>

                            {/* ================================================= */}
                            {/* EMAIL */}
                            {/* ================================================= */}

                            <div className="mb-3">

                                <label className="form-label">
                                    User Email Id{" "}
                                    <span className="text-danger">
                                        *
                                    </span>
                                </label>

                                <input
                                    type="email"
                                    className={`form-control ${errors.email
                                        ? "is-invalid"
                                        : ""
                                        }`}
                                    value={
                                        userForm.email
                                    }
                                    disabled={
                                        isReadOnly ||
                                        isEdit
                                    }
                                    onChange={(e) =>
                                        handleChange(
                                            "email",
                                            e.target.value
                                        )
                                    }
                                />

                                {errors.email && (
                                    <div className="invalid-feedback">
                                        {errors.email}
                                    </div>
                                )}

                            </div>

                            {/* ================================================= */}
                            {/* PRIMARY ROLE */}
                            {/* ================================================= */}

                            <div className="mb-3">

                                <label className="form-label">
                                    Primary Role{" "}
                                    <span className="text-danger">
                                        *
                                    </span>
                                </label>

                                <Select
                                    options={roleOptions}
                                    value={userForm.role}
                                    onChange={(option) => {

                                        const secondaryOptions =
                                            getSecondaryRoleOptions(
                                                option
                                            );

                                        setUserForm((prev) => ({
                                            ...prev,
                                            role: option,
                                            reportingUser: null,

                                            // CLEAR MULTIPLE SECONDARY ROLES
                                            secondaryRole: []
                                        }));

                                        setSecondaryRoleOptions(
                                            secondaryOptions
                                        );

                                        setErrors((prev) => ({
                                            ...prev,
                                            secondaryRole: "",
                                            reportingUser: ""
                                        }));

                                        validateField(
                                            "role",
                                            option
                                        );

                                        if (
                                            !superAdmin &&
                                            option?.value
                                        ) {
                                            getReportingUsers(
                                                option.value
                                            );
                                        }
                                    }}
                                    isDisabled={
                                        isReadOnly
                                    }
                                    placeholder="Select Role"
                                    isClearable
                                    styles={
                                        customSelectStyles
                                    }
                                    classNamePrefix="react-select"
                                    menuPlacement="auto"
                                />

                                {errors.role && (
                                    <small className="text-danger">
                                        {errors.role}
                                    </small>
                                )}

                            </div>

                            {/* ================================================= */}
                            {/* REPORTING HIERARCHY */}
                            {/* ================================================= */}

                            {!superAdmin && (
                                <div className="mb-3">

                                    <label className="form-label">
                                        Reporting To{" "}
                                        <span className="text-danger">
                                            *
                                        </span>
                                    </label>

                                    <Select
                                        options={
                                            reportingOptions
                                        }
                                        value={
                                            userForm.reportingUser
                                        }
                                        onChange={(
                                            option
                                        ) =>
                                            handleChange(
                                                "reportingUser",
                                                option
                                            )
                                        }
                                        isDisabled={
                                            isReadOnly
                                        }
                                        placeholder="Select Reporting User"
                                        styles={
                                            customSelectStyles
                                        }
                                        classNamePrefix="react-select"
                                        menuPlacement="auto"
                                    />

                                    {errors.reportingUser && (
                                        <small className="text-danger">
                                            {
                                                errors.reportingUser
                                            }
                                        </small>
                                    )}

                                </div>
                            )}

                            {/* ================================================= */}
                            {/* SECONDARY ROLE - MULTI SELECT */}
                            {/* ================================================= */}

                            {!superAdmin &&
                                secondaryRoleOptions.length > 0 && (
                                    <div className="mb-3">

                                        <label className="form-label">
                                            Secondary Role{" "}
                                            <span className="text-danger">
                                                *
                                            </span>
                                        </label>

                                        <Select
                                            isMulti
                                            options={
                                                secondaryRoleOptions
                                            }

                                            // IMPORTANT:
                                            // react-select multi select expects array
                                            value={
                                                Array.isArray(
                                                    userForm.secondaryRole
                                                )
                                                    ? userForm.secondaryRole
                                                    : []
                                            }

                                            onChange={(
                                                options
                                            ) => {

                                                const selectedOptions =
                                                    options || [];

                                                setUserForm(
                                                    (prev) => ({
                                                        ...prev,
                                                        secondaryRole:
                                                            selectedOptions
                                                    })
                                                );

                                                setErrors(
                                                    (prev) => ({
                                                        ...prev,
                                                        secondaryRole:
                                                            ""
                                                    })
                                                );
                                            }}

                                            isDisabled={
                                                isReadOnly
                                            }

                                            placeholder="Select Secondary Roles"

                                            closeMenuOnSelect={
                                                false
                                            }

                                            hideSelectedOptions={
                                                false
                                            }

                                            isClearable

                                            styles={
                                                customSelectStyles
                                            }

                                            classNamePrefix="react-select"

                                            menuPlacement="auto"
                                        />

                                        {errors.secondaryRole && (
                                            <small className="text-danger">
                                                {
                                                    errors.secondaryRole
                                                }
                                            </small>
                                        )}

                                    </div>
                                )}

                            {/* ================================================= */}
                            {/* DEPARTMENT */}
                            {/* ================================================= */}

                            {/* {!superAdmin && (
                                <div className="mb-3">

                                    <label className="form-label">
                                        Department{" "}
                                        <span className="text-danger">
                                            *
                                        </span>
                                    </label>

                                    <Select
                                        options={department}
                                        value={userForm.department}
                                        onChange={(option) =>
                                            handleChange(
                                                "department",
                                                option
                                            )
                                        }
                                        isDisabled={
                                            isReadOnly
                                        }
                                        placeholder="Select Department"
                                        styles={
                                            customSelectStyles
                                        }
                                        classNamePrefix="react-select"
                                        menuPlacement="auto"
                                    />

                                    {errors.department && (
                                        <small className="text-danger">
                                            {errors.department}
                                        </small>
                                    )}

                                </div>
                            )} */}

                            {/* ================================================= */}
                            {/* CAMPAIGN */}
                            {/* ================================================= */}

                            {/* {!superAdmin && (
                                <div className="mb-3">

                                    <label className="form-label">
                                        Campaign Assignment{" "}
                                        <span className="text-danger">
                                            *
                                        </span>
                                    </label>

                                    <Select
                                        options={
                                            campaignOptions
                                        }
                                        value={
                                            userForm.campaign
                                        }
                                        onChange={(
                                            option
                                        ) =>
                                            handleChange(
                                                "campaign",
                                                option
                                            )
                                        }
                                        isDisabled={
                                            isReadOnly
                                        }
                                        placeholder="Select Campaign"
                                        styles={
                                            customSelectStyles
                                        }
                                        classNamePrefix="react-select"
                                        menuPlacement="auto"
                                    />

                                    {errors.campaign && (
                                        <small className="text-danger">
                                            {
                                                errors.campaign
                                            }
                                        </small>
                                    )}

                                </div>
                            )} */}

                            {/* ================================================= */}
                            {/* STATUS */}
                            {/* ================================================= */}

                            {mode !== "add" &&
                                userData?.status !==
                                "unconfirmed" &&
                                userData?.status !==
                                "deleted" && (

                                    <div className="mb-3">

                                        <label className="form-label">
                                            Status{" "}
                                            <span className="text-danger">
                                                *
                                            </span>
                                        </label>

                                        <Select
                                            options={
                                                statusOptions
                                            }
                                            value={
                                                userForm.status
                                            }
                                            onChange={(
                                                option
                                            ) =>
                                                handleChange(
                                                    "status",
                                                    option
                                                )
                                            }
                                            isDisabled={
                                                isReadOnly
                                            }
                                            placeholder="Select Status"
                                            classNamePrefix="react-select"
                                        />

                                        {errors.status && (
                                            <small className="text-danger">
                                                {
                                                    errors.status
                                                }
                                            </small>
                                        )}

                                    </div>
                                )}

                            {/* ================================================= */}
                            {/* BUTTONS */}
                            {/* ================================================= */}

                            {!isReadOnly && (
                                <div className="d-flex gap-2 mt-4">

                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={
                                            isLoading ||
                                            (
                                                isEdit &&
                                                !isFormChanged()
                                            ) ||
                                            (
                                                !isEdit &&
                                                !isFormValid
                                            )
                                        }
                                    >
                                        {isLoading
                                            ? "Creating..."
                                            : mode ===
                                                "add"
                                                ? "Create User"
                                                : "Update User"}
                                    </button>

                                    {mode === "add" && (
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={
                                                handleReset
                                            }
                                            disabled={
                                                isLoading
                                            }
                                        >
                                            Reset
                                        </button>
                                    )}

                                </div>
                            )}

                        </fieldset>

                    </form>

                </div>

            </div>

            {/* ================================================= */}
            {/* BACKDROP */}
            {/* ================================================= */}

            <div
                className="offcanvas-backdrop fade show"
                onClick={onClose}
            ></div>
        </>
    );
};

export default UserDrawer;